import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Navbar from '../components/Navbar';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import {
  Settings, Tag, Users, Megaphone, Palette, Shield,
  Plus, Trash2, Edit, Save, RefreshCw, Search,
  Crown, Zap, Mail, AlertTriangle, BarChart3,
  Gift, Eye, ChevronRight, X,
  TrendingUp, Bell, DollarSign, FileText,
  CheckSquare, Clock, IndianRupee
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ title, icon: Icon, children, description }) => (
  <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden mb-6">
    <div className="flex items-center gap-3 px-6 py-4 border-b border-gray-100 dark:border-slate-700 bg-gray-50/50 dark:bg-slate-700/30">
      <div className="w-8 h-8 rounded-lg bg-cyan-100 dark:bg-cyan-900/30 flex items-center justify-center">
        <Icon className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
      </div>
      <div>
        <h3 className="font-semibold text-gray-900 dark:text-white text-sm">{title}</h3>
        {description && <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">{description}</p>}
      </div>
    </div>
    <div className="p-6">{children}</div>
  </div>
);

const Field = ({ label, hint, children }) => (
  <div className="space-y-1.5">
    <Label className="text-sm font-medium text-gray-700 dark:text-slate-300">{label}</Label>
    {hint && <p className="text-xs text-gray-400 dark:text-slate-500">{hint}</p>}
    {children}
  </div>
);

const Toggle = ({ label, description, checked, onChange }) => (
  <div className="flex items-center justify-between py-3 border-b border-gray-100 dark:border-slate-700 last:border-0">
    <div>
      <p className="text-sm font-medium text-gray-800 dark:text-slate-200">{label}</p>
      {description && <p className="text-xs text-gray-400 dark:text-slate-500 mt-0.5">{description}</p>}
    </div>
    <Switch checked={checked} onCheckedChange={onChange} />
  </div>
);

const THEMES = [
  { id: 'default', label: 'Default', emoji: '🌐' },
  { id: 'christmas', label: 'Christmas', emoji: '🎄' },
  { id: 'bw', label: 'Black & White', emoji: '🖤' },
  { id: 'diwali', label: 'Diwali', emoji: '🪔' },
  { id: 'newyear', label: 'New Year', emoji: '🎆' },
  { id: 'valentine', label: 'Valentine', emoji: '❤️' },
];

export default function ControlPanel() {
  const { token } = useAuth();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [local, setLocal] = useState({});

  // Coupons
  const [coupons, setCoupons] = useState([]);
  const [showCouponDialog, setShowCouponDialog] = useState(false);
  const [newCoupon, setNewCoupon] = useState({
    code: '', discount_type: 'percent', discount_value: 10,
    applicable_to: 'both', max_uses: '', per_user_limit: 1,
    expires_at: '', active: true
  });

  // User management
  const [userSearch, setUserSearch] = useState('');
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [userDetail, setUserDetail] = useState(null);
  const [grantDialog, setGrantDialog] = useState(null); // {type: 'credits'|'premium', userId}
  const [grantAmount, setGrantAmount] = useState('');

  // Broadcast
  const [broadcast, setBroadcast] = useState({ subject: '', body: '', segment: 'all' });
  const [sendingBroadcast, setSendingBroadcast] = useState(false);

  // Blacklist
  const [newWord, setNewWord] = useState('');

  // Analytics
  const [analytics, setAnalytics] = useState(null);
  const [analyticsLoading, setAnalyticsLoading] = useState(false);

  // Push notification broadcast
  const [pushMsg, setPushMsg] = useState({ message: '', segment: 'all' });
  const [sendingPush, setSendingPush] = useState(false);

  // Refund log
  const [refunds, setRefunds] = useState([]);
  const [newRefund, setNewRefund] = useState({ payment_id: '', user_email: '', amount_inr: '', reason: '' });
  const [refundLoading, setRefundLoading] = useState(false);

  // Payment failures
  const [paymentFailures, setPaymentFailures] = useState([]);

  // Email templates
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [editingTemplate, setEditingTemplate] = useState(null);

  // Revenue goal
  const [revenueGoal, setRevenueGoal] = useState('');

  // Bulk actions
  const [bulkIds, setBulkIds] = useState('');
  const [bulkAction, setBulkAction] = useState('ban');
  const [bulkReason, setBulkReason] = useState('');

  const loadConfig = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/control-panel/config`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(res.data);
      setLocal(res.data);
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || 'Failed to load config';
      toast.error(detail);
      console.error('loadConfig error:', err);
    } finally { setLoading(false); }
  }, [token]);

  const loadCoupons = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/control-panel/coupons`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCoupons(res.data.coupons);
    } catch { toast.error('Failed to load coupons'); }
  }, [token]);

  const loadAnalytics = useCallback(async () => {
    setAnalyticsLoading(true);
    try {
      const res = await axios.get(`${API}/control-panel/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnalytics(res.data);
    } catch (err) {
      console.error('Analytics load failed:', err);
      // Don't toast — analytics loads silently
    } finally { setAnalyticsLoading(false); }
  }, [token]);

  const loadRefunds = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/control-panel/refund-logs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRefunds(res.data.refunds);
    } catch {}
  }, [token]);

  const loadPaymentFailures = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/control-panel/payment-failures`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPaymentFailures(res.data.failures);
    } catch {}
  }, [token]);

  const loadEmailTemplates = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/control-panel/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEmailTemplates(res.data.templates);
    } catch {}
  }, [token]);

  useEffect(() => {
    loadConfig();
    loadCoupons();
    loadAnalytics();
    loadRefunds();
    loadPaymentFailures();
    loadEmailTemplates();
  }, [loadConfig, loadCoupons, loadAnalytics, loadRefunds, loadPaymentFailures, loadEmailTemplates]);

  const save = async (fields) => {
    if (!fields || Object.keys(fields).length === 0) return;
    setSaving(true);
    try {
      await axios.patch(`${API}/control-panel/config`, fields, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConfig(prev => ({ ...prev, ...fields }));
      setLocal(prev => ({ ...prev, ...fields }));
      toast.success('Saved ✓');
    } catch (err) {
      const msg = err.response?.data?.detail || 'Save failed';
      toast.error(msg);
      console.error('save error:', err);
    } finally { setSaving(false); }
  };

  const handleToggle = (key, value) => {
    // value is always a boolean from Switch component
    const boolVal = Boolean(value);
    setLocal(prev => ({ ...prev, [key]: boolVal }));
    save({ [key]: boolVal });
  };

  const handleSaveField = (keys) => {
    const fields = {};
    // Only include fields that are actually defined (avoids sending undefined)
    keys.forEach(k => {
      if (local[k] !== undefined) fields[k] = local[k];
    });
    if (Object.keys(fields).length > 0) save(fields);
  };

  const searchUsers = async () => {
    if (!userSearch.trim()) return;
    setUserLoading(true);
    try {
      const res = await axios.get(`${API}/control-panel/users?q=${encodeURIComponent(userSearch)}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(res.data.users);
    } catch { toast.error('Search failed'); }
    finally { setUserLoading(false); }
  };

  const loadUserDetail = async (userId) => {
    setSelectedUser(userId);
    try {
      const res = await axios.get(`${API}/control-panel/users/${userId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserDetail(res.data);
    } catch { toast.error('Failed to load user'); }
  };

  const handleGrant = async () => {
    if (!grantDialog || !grantAmount) return;
    try {
      const endpoint = grantDialog.type === 'credits'
        ? `${API}/control-panel/users/${grantDialog.userId}/grant-credits`
        : `${API}/control-panel/users/${grantDialog.userId}/grant-premium`;
      const body = grantDialog.type === 'credits'
        ? { amount: parseInt(grantAmount) }
        : { days: parseInt(grantAmount) };
      await axios.post(endpoint, body, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${grantDialog.type === 'credits' ? 'Credits' : 'Premium'} granted!`);
      setGrantDialog(null);
      setGrantAmount('');
      if (selectedUser) loadUserDetail(selectedUser);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  const createCoupon = async () => {
    try {
      await axios.post(`${API}/control-panel/coupons`, {
        ...newCoupon,
        discount_value: parseFloat(newCoupon.discount_value),
        max_uses: newCoupon.max_uses ? parseInt(newCoupon.max_uses) : null,
        expires_at: newCoupon.expires_at || null,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Coupon created');
      setShowCouponDialog(false);
      setNewCoupon({ code: '', discount_type: 'percent', discount_value: 10, applicable_to: 'both', max_uses: '', per_user_limit: 1, expires_at: '', active: true });
      loadCoupons();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create coupon'); }
  };

  const deleteCoupon = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    await axios.delete(`${API}/control-panel/coupons/${id}`, { headers: { Authorization: `Bearer ${token}` } });
    toast.success('Coupon deleted');
    loadCoupons();
  };

  const toggleCoupon = async (id, active) => {
    await axios.patch(`${API}/control-panel/coupons/${id}`, { active }, { headers: { Authorization: `Bearer ${token}` } });
    loadCoupons();
  };

  const sendBroadcast = async () => {
    if (!broadcast.subject || !broadcast.body) { toast.error('Subject and body required'); return; }
    setSendingBroadcast(true);
    try {
      const res = await axios.post(`${API}/control-panel/broadcast-email`, broadcast, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setBroadcast({ subject: '', body: '', segment: 'all' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Broadcast failed'); }
    finally { setSendingBroadcast(false); }
  };

  const addBlacklistWord = () => {
    if (!newWord.trim()) return;
    const words = [...(local.blacklist_words || []), newWord.trim().toLowerCase()];
    setLocal(prev => ({ ...prev, blacklist_words: words }));
    save({ blacklist_words: words });
    setNewWord('');
  };

  const sendPushNotification = async () => {
    if (!pushMsg.message.trim()) { toast.error('Message required'); return; }
    setSendingPush(true);
    try {
      const res = await axios.post(`${API}/control-panel/broadcast-notification`, pushMsg, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(res.data.message);
      setPushMsg({ message: '', segment: 'all' });
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSendingPush(false); }
  };

  const logRefund = async () => {
    if (!newRefund.payment_id || !newRefund.user_email || !newRefund.amount_inr) {
      toast.error('Payment ID, email and amount required'); return;
    }
    setRefundLoading(true);
    try {
      await axios.post(`${API}/control-panel/refund-log`, newRefund, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Refund logged');
      setNewRefund({ payment_id: '', user_email: '', amount_inr: '', reason: '' });
      loadRefunds();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setRefundLoading(false); }
  };

  const saveRevenueGoal = async () => {
    if (!revenueGoal) return;
    await axios.post(`${API}/control-panel/revenue-goal`, { goal_inr: parseFloat(revenueGoal) }, {
      headers: { Authorization: `Bearer ${token}` }
    });
    toast.success('Revenue goal saved');
  };

  const saveEmailTemplate = async (template) => {
    await axios.put(`${API}/control-panel/email-templates/${template.id}`, template, {
      headers: { Authorization: `Bearer ${token}` }
    });
    toast.success('Template saved');
    setEditingTemplate(null);
    loadEmailTemplates();
  };

  const executeBulkAction = async () => {
    const ids = bulkIds.split(/[\n,]/).map(s => s.trim()).filter(Boolean);
    if (!ids.length) { toast.error('Enter at least one user ID'); return; }
    if (!window.confirm(`Apply "${bulkAction}" to ${ids.length} users?`)) return;
    try {
      const res = await axios.post(`${API}/control-panel/users/bulk-action`,
        { user_ids: ids, action: bulkAction, reason: bulkReason || 'Bulk action' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setBulkIds(''); setBulkReason('');
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk action failed'); }
  };

  const removeBlacklistWord = (word) => {
    const words = (local.blacklist_words || []).filter(w => w !== word);
    setLocal(prev => ({ ...prev, blacklist_words: words }));
    save({ blacklist_words: words });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50 dark:bg-slate-900">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-cyan-500" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 transition-colors">
      <Navbar />
      <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">

          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Control Panel</h1>
              <p className="text-gray-500 dark:text-slate-400 text-sm mt-0.5">Manage your app without touching code</p>
            </div>
            <Button onClick={loadConfig} variant="outline" size="sm" className="gap-1.5 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </Button>
          </div>

          <Tabs defaultValue="status" className="w-full">
            <TabsList className="flex flex-wrap gap-1 h-auto mb-8 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 p-1.5 rounded-2xl">
              {[
                { value: 'status', icon: Shield, label: 'App Status' },
                { value: 'offers', icon: Tag, label: 'Offers & Pricing' },
                { value: 'coupons', icon: Gift, label: 'Coupons' },
                { value: 'content', icon: Settings, label: 'Content' },
                { value: 'announce', icon: Megaphone, label: 'Announcements' },
                { value: 'theme', icon: Palette, label: 'Themes' },
                { value: 'users', icon: Users, label: 'Users' },
                { value: 'safety', icon: AlertTriangle, label: 'Safety' },
                { value: 'broadcast', icon: Mail, label: 'Broadcast' },
                { value: 'analytics', icon: BarChart3, label: 'Analytics' },
                { value: 'payments', icon: DollarSign, label: 'Payments' },
                { value: 'templates', icon: FileText, label: 'Templates' },
                { value: 'bulk', icon: CheckSquare, label: 'Bulk Actions' },
              ].map(({ value, icon: Icon, label }) => (
                <TabsTrigger key={value} value={value}
                  className="rounded-xl text-xs font-medium px-3 py-2 text-gray-600 dark:text-slate-400 data-[state=active]:bg-cyan-600 data-[state=active]:text-white data-[state=active]:shadow-sm transition-all">
                  <Icon className="w-3.5 h-3.5 mr-1.5" />{label}
                </TabsTrigger>
              ))}
            </TabsList>

            {/* ── APP STATUS ── */}
            <TabsContent value="status">
              <Section title="App Status" icon={Shield} description="Control app-wide operational modes">
                <Toggle label="Maintenance Mode" description="Only admins can access the app — shows maintenance page to users"
                  checked={!!local.maintenance_mode} onChange={v => handleToggle('maintenance_mode', v)} />
                <Toggle label="Read-Only Mode" description="Users can browse but cannot post, vote, or comment"
                  checked={!!local.read_only_mode} onChange={v => handleToggle('read_only_mode', v)} />
                <Toggle label="Registration Open" description="Allow new users to sign up"
                  checked={!!local.registration_open} onChange={v => handleToggle('registration_open', v)} />
              </Section>
              <Section title="App Version" icon={Settings}>
                <div className="flex gap-3">
                  <Input value={local.app_version || ''} onChange={e => setLocal(p => ({ ...p, app_version: e.target.value }))}
                    placeholder="e.g. 1.2.0" className="max-w-xs dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  <Button onClick={() => handleSaveField(['app_version'])} size="sm" disabled={saving}>
                    <Save className="w-4 h-4 mr-1.5" /> Save
                  </Button>
                </div>
              </Section>
            </TabsContent>

            {/* ── OFFERS & PRICING ── */}
            <TabsContent value="offers">
              <Section title="Offer Toggle" icon={Tag} description="Enable or disable sitewide offer display">
                <Toggle label="Show Offers" description="Display offer badges and discounted prices on payment page"
                  checked={!!local.offer_active} onChange={v => handleToggle('offer_active', v)} />
              </Section>

              <Section title="Premium Pricing" icon={Crown} description="Prices in INR">
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Current Price (₹)" hint="Discounted price shown to users">
                    <Input type="number" value={local.premium_price_inr || ''} onChange={e => setLocal(p => ({ ...p, premium_price_inr: parseFloat(e.target.value) }))}
                      className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                  <Field label="Original Price (₹)" hint="Struck-through original price">
                    <Input type="number" value={local.premium_original_price_inr || ''} onChange={e => setLocal(p => ({ ...p, premium_original_price_inr: parseFloat(e.target.value) }))}
                      className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                  <Field label="Offer Badge Text" hint='e.g. "🔥 Flash Sale!"'>
                    <Input value={local.offer_label_premium || ''} onChange={e => setLocal(p => ({ ...p, offer_label_premium: e.target.value }))}
                      className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                  <Field label="Offer End Date" hint="Leave blank for no expiry">
                    <Input type="date" value={local.offer_end_date?.split('T')[0] || ''} onChange={e => setLocal(p => ({ ...p, offer_end_date: e.target.value }))}
                      className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                </div>
                <Button onClick={() => handleSaveField(['premium_price_inr', 'premium_original_price_inr', 'offer_label_premium', 'offer_end_date'])} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-1.5" /> Save Premium Pricing
                </Button>
              </Section>

              <Section title="Promotion Credit Pricing" icon={Zap}>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Current Price (₹)">
                    <Input type="number" value={local.promo_price_inr || ''} onChange={e => setLocal(p => ({ ...p, promo_price_inr: parseFloat(e.target.value) }))}
                      className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                  <Field label="Original Price (₹)">
                    <Input type="number" value={local.promo_original_price_inr || ''} onChange={e => setLocal(p => ({ ...p, promo_original_price_inr: parseFloat(e.target.value) }))}
                      className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                  <Field label="Offer Badge Text">
                    <Input value={local.offer_label_promo || ''} onChange={e => setLocal(p => ({ ...p, offer_label_promo: e.target.value }))}
                      className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                </div>
                <Button onClick={() => handleSaveField(['promo_price_inr', 'promo_original_price_inr', 'offer_label_promo'])} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-1.5" /> Save Promo Pricing
                </Button>
              </Section>
            </TabsContent>

            {/* ── COUPONS ── */}
            <TabsContent value="coupons">
              <div className="flex justify-between items-center mb-4">
                <p className="text-sm text-gray-500 dark:text-slate-400">{coupons.length} coupons</p>
                <Button onClick={() => setShowCouponDialog(true)} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white">
                  <Plus className="w-4 h-4 mr-1.5" /> New Coupon
                </Button>
              </div>

              {coupons.length === 0 ? (
                <div className="text-center py-16 bg-white dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700">
                  <Gift className="w-12 h-12 text-gray-200 dark:text-slate-700 mx-auto mb-3" />
                  <p className="text-gray-400 dark:text-slate-500">No coupons yet</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {coupons.map(c => (
                    <div key={c.id} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 flex items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`px-3 py-1 rounded-lg font-mono text-sm font-bold ${c.active ? 'bg-cyan-100 dark:bg-cyan-900/30 text-cyan-700 dark:text-cyan-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-400 line-through'}`}>
                          {c.code}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-gray-900 dark:text-white">
                            {c.discount_type === 'percent' ? `${c.discount_value}% off` : `₹${c.discount_value} off`}
                            <span className="text-xs text-gray-400 ml-2">· {c.applicable_to}</span>
                          </p>
                          <p className="text-xs text-gray-400 dark:text-slate-500">
                            {c.use_count || 0} uses{c.max_uses ? ` / ${c.max_uses}` : ''}{c.expires_at ? ` · expires ${new Date(c.expires_at).toLocaleDateString()}` : ''}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Switch checked={c.active} onCheckedChange={v => toggleCoupon(c.id, v)} />
                        <button onClick={() => deleteCoupon(c.id)} className="p-1.5 text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* ── CONTENT LIMITS ── */}
            <TabsContent value="content">
              <Section title="Post Limits" icon={Settings} description="Daily posting limits per user type">
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Free users — posts/day"><Input type="number" value={local.free_posts_per_day || ''} onChange={e => setLocal(p => ({ ...p, free_posts_per_day: parseInt(e.target.value) }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="Premium users — posts/day"><Input type="number" value={local.premium_posts_per_day || ''} onChange={e => setLocal(p => ({ ...p, premium_posts_per_day: parseInt(e.target.value) }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="Post max characters"><Input type="number" value={local.post_max_chars || ''} onChange={e => setLocal(p => ({ ...p, post_max_chars: parseInt(e.target.value) }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="Max comments per post"><Input type="number" value={local.max_comments_per_post || ''} onChange={e => setLocal(p => ({ ...p, max_comments_per_post: parseInt(e.target.value) }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="Free users — save limit"><Input type="number" value={local.free_saves_limit || ''} onChange={e => setLocal(p => ({ ...p, free_saves_limit: parseInt(e.target.value) }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="Premium users — save limit"><Input type="number" value={local.premium_saves_limit || ''} onChange={e => setLocal(p => ({ ...p, premium_saves_limit: parseInt(e.target.value) }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                </div>
                <Button onClick={() => handleSaveField(['free_posts_per_day', 'premium_posts_per_day', 'post_max_chars', 'max_comments_per_post', 'free_saves_limit', 'premium_saves_limit'])} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-1.5" /> Save Limits
                </Button>
              </Section>
              <Section title="Post & Content Settings" icon={Clock}>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Post expiry (days)" hint="How many days posts stay in feed rotation">
                    <Input type="number" value={local.post_expiry_days || 7} onChange={e => setLocal(p => ({ ...p, post_expiry_days: parseInt(e.target.value) }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                  <Field label="Featured post ID" hint="Pin this post to the top of everyone's feed">
                    <Input value={local.featured_post_id || ''} onChange={e => setLocal(p => ({ ...p, featured_post_id: e.target.value }))} placeholder="Post UUID (leave blank to disable)" className="font-mono text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                </div>
                <Button onClick={() => handleSaveField(['post_expiry_days', 'featured_post_id'])} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-1.5" /> Save
                </Button>
              </Section>

              <Section title="Notifications" icon={Settings}>
                <Toggle label="Vote notifications" checked={!!local.notif_votes_enabled} onChange={v => handleToggle('notif_votes_enabled', v)} />
                <Toggle label="Comment notifications" checked={!!local.notif_comments_enabled} onChange={v => handleToggle('notif_comments_enabled', v)} />
                <div className="mt-4">
                  <Field label="Notification cooldown (minutes)" hint="Minimum gap between notifications to the same user">
                    <div className="flex gap-3">
                      <Input type="number" value={local.notif_cooldown_minutes || 0} onChange={e => setLocal(p => ({ ...p, notif_cooldown_minutes: parseInt(e.target.value) }))} className="max-w-xs dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                      <Button onClick={() => handleSaveField(['notif_cooldown_minutes'])} size="sm" disabled={saving}><Save className="w-4 h-4" /></Button>
                    </div>
                  </Field>
                </div>
              </Section>
            </TabsContent>

            {/* ── ANNOUNCEMENTS ── */}
            <TabsContent value="announce">
              <Section title="Sitewide Banner" icon={Megaphone} description="Shows a bar above the navbar for all users">
                <Toggle label="Show Banner" checked={!!local.announcement_active} onChange={v => handleToggle('announcement_active', v)} />
                <Toggle label="Dismissible" description="Users can close the banner" checked={!!local.announcement_dismissible} onChange={v => handleToggle('announcement_dismissible', v)} />
                <div className="grid sm:grid-cols-2 gap-4 mt-4 mb-4">
                  <Field label="Banner Text"><Input value={local.announcement_text || ''} onChange={e => setLocal(p => ({ ...p, announcement_text: e.target.value }))} placeholder="New feature launched! 🎉" className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="Link (optional)"><Input value={local.announcement_link || ''} onChange={e => setLocal(p => ({ ...p, announcement_link: e.target.value }))} placeholder="https://..." className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="Color" hint="cyan | blue | green | red | orange | purple | yellow">
                    <select value={local.announcement_color || 'cyan'} onChange={e => setLocal(p => ({ ...p, announcement_color: e.target.value }))}
                      className="w-full h-9 px-3 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm">
                      {['cyan','blue','green','red','orange','purple','yellow'].map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                </div>
                <Button onClick={() => handleSaveField(['announcement_text', 'announcement_link', 'announcement_color'])} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-1.5" /> Save Banner
                </Button>
              </Section>

              <Section title="Popup Announcement" icon={Megaphone} description="One-time modal shown to users on login">
                <Toggle label="Show Popup" checked={!!local.popup_active} onChange={v => handleToggle('popup_active', v)} />
                <div className="grid gap-4 mt-4 mb-4">
                  <Field label="Popup Title"><Input value={local.popup_title || ''} onChange={e => setLocal(p => ({ ...p, popup_title: e.target.value }))} placeholder="Important update!" className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="Popup Body">
                    <textarea value={local.popup_body || ''} onChange={e => setLocal(p => ({ ...p, popup_body: e.target.value }))} rows={4} placeholder="We've added new features..."
                      className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm resize-none" />
                  </Field>
                </div>
                <Button onClick={() => handleSaveField(['popup_title', 'popup_body'])} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-1.5" /> Save Popup
                </Button>
              </Section>
            </TabsContent>

            {/* ── THEMES ── */}
            <TabsContent value="theme">
              <Section title="Seasonal Theme" icon={Palette} description="Change the app's visual theme for special occasions">
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                  {THEMES.map(t => (
                    <button key={t.id} onClick={() => { setLocal(p => ({ ...p, theme_mode: t.id })); save({ theme_mode: t.id }); }}
                      className={`p-4 rounded-xl border-2 text-center transition-all ${local.theme_mode === t.id ? 'border-cyan-500 bg-cyan-50 dark:bg-cyan-900/20' : 'border-gray-200 dark:border-slate-700 hover:border-cyan-300 bg-white dark:bg-slate-800'}`}>
                      <div className="text-3xl mb-2">{t.emoji}</div>
                      <p className={`text-sm font-medium ${local.theme_mode === t.id ? 'text-cyan-700 dark:text-cyan-300' : 'text-gray-700 dark:text-slate-300'}`}>{t.label}</p>
                      {local.theme_mode === t.id && <p className="text-xs text-cyan-500 mt-1">Active</p>}
                    </button>
                  ))}
                </div>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Theme Start Date" hint="Auto-activate theme on this date">
                    <Input type="date" value={local.theme_start?.split('T')[0] || ''} onChange={e => setLocal(p => ({ ...p, theme_start: e.target.value }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                  <Field label="Theme End Date" hint="Auto-deactivate after this date">
                    <Input type="date" value={local.theme_end?.split('T')[0] || ''} onChange={e => setLocal(p => ({ ...p, theme_end: e.target.value }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                </div>
                <Button onClick={() => handleSaveField(['theme_start', 'theme_end'])} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-1.5" /> Save Schedule
                </Button>
              </Section>
            </TabsContent>

            {/* ── USER MANAGEMENT ── */}
            <TabsContent value="users">
              <Section title="Search Users" icon={Users}>
                <div className="flex gap-3 mb-4">
                  <Input value={userSearch} onChange={e => setUserSearch(e.target.value)} onKeyDown={e => e.key === 'Enter' && searchUsers()}
                    placeholder="Search by email or username…" className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  <Button onClick={searchUsers} disabled={userLoading} size="sm" className="bg-cyan-600 hover:bg-cyan-700 text-white px-4">
                    <Search className="w-4 h-4 mr-1.5" />{userLoading ? '…' : 'Search'}
                  </Button>
                </div>
                {users.length > 0 && (
                  <div className="space-y-2">
                    {users.map(u => (
                      <div key={u.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl border border-gray-100 dark:border-slate-700">
                        <div className="flex items-center gap-3">
                          <img src={u.avatar_url} className="w-9 h-9 rounded-full" alt="" />
                          <div>
                            <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                              {u.username}
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${u.role === 'premium' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' : 'bg-gray-100 text-gray-600 dark:bg-slate-600 dark:text-slate-300'}`}>{u.role}</span>
                              <span className={`text-xs px-1.5 py-0.5 rounded-full ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{u.status}</span>
                            </p>
                            <p className="text-xs text-gray-400">{u.email} · {u.post_count} posts · {u.report_count} reports rcvd</p>
                          </div>
                        </div>
                        <div className="flex gap-1.5">
                          <button onClick={() => loadUserDetail(u.id)} className="p-1.5 text-cyan-500 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 rounded-lg transition" title="View detail"><Eye className="w-4 h-4" /></button>
                          <button onClick={() => setGrantDialog({ type: 'credits', userId: u.id })} className="p-1.5 text-amber-500 hover:bg-amber-50 dark:hover:bg-amber-900/20 rounded-lg transition" title="Grant credits"><Zap className="w-4 h-4" /></button>
                          <button onClick={() => setGrantDialog({ type: 'premium', userId: u.id })} className="p-1.5 text-purple-500 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition" title="Grant premium"><Crown className="w-4 h-4" /></button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </TabsContent>

            {/* ── SAFETY ── */}
            <TabsContent value="safety">
              <Section title="Report Thresholds" icon={AlertTriangle}>
                <div className="grid sm:grid-cols-2 gap-4 mb-4">
                  <Field label="Reports to appear in admin queue" hint="Posts with ≥ this many reports show up for review">
                    <Input type="number" value={local.report_threshold || ''} onChange={e => setLocal(p => ({ ...p, report_threshold: parseInt(e.target.value) }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                  <Field label="Auto-shadow-ban after N reports">
                    <Input type="number" value={local.auto_ban_reports || ''} onChange={e => setLocal(p => ({ ...p, auto_ban_reports: parseInt(e.target.value) }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                  <Field label="Within Y days" hint="Reports within this window count toward auto-ban">
                    <Input type="number" value={local.auto_ban_days || ''} onChange={e => setLocal(p => ({ ...p, auto_ban_days: parseInt(e.target.value) }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  </Field>
                </div>
                <Button onClick={() => handleSaveField(['report_threshold', 'auto_ban_reports', 'auto_ban_days'])} disabled={saving} size="sm">
                  <Save className="w-4 h-4 mr-1.5" /> Save Thresholds
                </Button>
              </Section>

              <Section title="Word Blacklist" icon={Shield} description="Posts containing these words will be auto-rejected">
                <div className="flex gap-3 mb-4">
                  <Input value={newWord} onChange={e => setNewWord(e.target.value)} onKeyDown={e => e.key === 'Enter' && addBlacklistWord()}
                    placeholder="Add a banned word or phrase…" className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  <Button onClick={addBlacklistWord} size="sm" className="bg-red-600 hover:bg-red-700 text-white px-4">
                    <Plus className="w-4 h-4 mr-1" /> Add
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {(local.blacklist_words || []).length === 0
                    ? <p className="text-sm text-gray-400 dark:text-slate-500">No words blacklisted yet</p>
                    : (local.blacklist_words || []).map(w => (
                      <span key={w} className="inline-flex items-center gap-1.5 px-3 py-1 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 rounded-full text-sm">
                        {w}
                        <button onClick={() => removeBlacklistWord(w)} className="hover:text-red-900"><X className="w-3 h-3" /></button>
                      </span>
                    ))
                  }
                </div>
              </Section>
            </TabsContent>

            {/* ── ANALYTICS ── */}
            <TabsContent value="analytics">
              <div className="flex justify-end mb-4">
                <Button onClick={loadAnalytics} variant="outline" size="sm" disabled={analyticsLoading} className="gap-1.5 border-gray-200 dark:border-slate-700 text-gray-600 dark:text-slate-300">
                  <RefreshCw className={`w-3.5 h-3.5 ${analyticsLoading ? 'animate-spin' : ''}`} /> Refresh
                </Button>
              </div>
              {analytics ? (
                <>
                  {/* Stat cards */}
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
                    {[
                      { label: 'Total Users', value: analytics.totals.users, sub: `+${analytics.today.users} today`, color: 'text-cyan-600' },
                      { label: 'Total Posts', value: analytics.totals.posts, sub: `+${analytics.today.posts} today`, color: 'text-blue-600' },
                      { label: 'Total Votes', value: analytics.totals.votes, sub: `+${analytics.today.votes} today`, color: 'text-purple-600' },
                      { label: 'Premium Users', value: analytics.totals.premium_users, sub: `${analytics.totals.banned_users} banned`, color: 'text-yellow-600' },
                    ].map(({ label, value, sub, color }) => (
                      <div key={label} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4 text-center">
                        <p className={`text-2xl font-bold ${color}`}>{value.toLocaleString()}</p>
                        <p className="text-xs font-medium text-gray-500 dark:text-slate-400 mt-0.5">{label}</p>
                        <p className="text-xs text-gray-400 dark:text-slate-500 mt-1">{sub}</p>
                      </div>
                    ))}
                  </div>
                  {/* Period comparison */}
                  <div className="grid sm:grid-cols-3 gap-4 mb-6">
                    {[
                      { period: 'Today', data: analytics.today || {} },
                      { period: 'This Week', data: analytics.week || {} },
                      { period: 'This Month', data: analytics.month || {} },
                    ].map(({ period, data }) => (
                      <div key={period} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                        <p className="text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-3">{period}</p>
                        <div className="space-y-2">
                          <div className="flex justify-between text-sm"><span className="text-gray-600 dark:text-slate-300">New users</span><span className="font-semibold text-gray-900 dark:text-white">+{data.users}</span></div>
                          <div className="flex justify-between text-sm"><span className="text-gray-600 dark:text-slate-300">New posts</span><span className="font-semibold text-gray-900 dark:text-white">+{data.posts}</span></div>
                          <div className="flex justify-between text-sm"><span className="text-gray-600 dark:text-slate-300">Votes cast</span><span className="font-semibold text-gray-900 dark:text-white">+{data.votes}</span></div>
                          {data.revenue_inr !== undefined && (
                            <div className="flex justify-between text-sm border-t border-gray-100 dark:border-slate-700 pt-2 mt-2">
                              <span className="text-gray-600 dark:text-slate-300">Revenue</span>
                              <span className="font-bold text-green-600">₹{data.revenue_inr.toFixed(2)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  {/* Sparkline-style chart (text-based) */}
                  <div className="grid sm:grid-cols-2 gap-4">
                    {[
                      { title: 'Daily Signups (14d)', data: analytics.charts.daily_signups },
                      { title: 'Daily Posts (14d)', data: analytics.charts.daily_posts },
                    ].map(({ title, data }) => {
                      const max = Math.max(...data.map(d => d.count), 1);
                      return (
                        <div key={title} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                          <p className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">{title}</p>
                          <div className="flex items-end gap-1 h-20">
                            {data.map((d, i) => (
                              <div key={i} className="flex-1 flex flex-col items-center gap-1">
                                <div className="w-full bg-cyan-500 dark:bg-cyan-600 rounded-sm transition-all"
                                  style={{ height: `${Math.max((d.count / max) * 72, 2)}px` }}
                                  title={`${d.date}: ${d.count}`}
                                />
                              </div>
                            ))}
                          </div>
                          <div className="flex justify-between text-xs text-gray-400 dark:text-slate-500 mt-1">
                            <span>{data[0]?.date}</span><span>{data[data.length-1]?.date}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </>
              ) : (
                <div className="text-center py-16 text-gray-400 dark:text-slate-500">
                  {analyticsLoading ? 'Loading analytics…' : 'Click Refresh to load analytics'}
                </div>
              )}
            </TabsContent>

            {/* ── PAYMENTS TAB ── */}
            <TabsContent value="payments">
              <Section title="Revenue Goal" icon={IndianRupee} description="Set a monthly revenue target and track progress">
                {analytics && (
                  <div className="mb-4">
                    <div className="flex items-center justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-slate-300">This month: ₹{analytics.month.revenue_inr?.toFixed(2) || '0.00'}</span>
                      {local.revenue_goal_inr && <span className="text-gray-500 dark:text-slate-400">Goal: ₹{local.revenue_goal_inr}</span>}
                    </div>
                    {local.revenue_goal_inr && (
                      <div className="h-3 bg-gray-200 dark:bg-slate-700 rounded-full overflow-hidden mb-3">
                        <div className="h-full bg-green-500 transition-all duration-500 rounded-full"
                          style={{ width: `${Math.min((analytics.month.revenue_inr / local.revenue_goal_inr) * 100, 100)}%` }} />
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-3">
                  <Input type="number" value={revenueGoal || local.revenue_goal_inr || ''} onChange={e => setRevenueGoal(e.target.value)}
                    placeholder="e.g. 5000" className="max-w-xs dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                  <Button onClick={saveRevenueGoal} size="sm" disabled={saving}><Save className="w-4 h-4 mr-1.5" /> Set Goal</Button>
                </div>
              </Section>

              <Section title="Log a Manual Refund" icon={DollarSign} description="Record refunds issued outside the platform (for your records)">
                <div className="grid sm:grid-cols-2 gap-3 mb-4">
                  <Field label="Payment/Order ID"><Input value={newRefund.payment_id} onChange={e => setNewRefund(p => ({ ...p, payment_id: e.target.value }))} placeholder="order_xxx or pay_xxx" className="font-mono text-sm dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="User Email"><Input value={newRefund.user_email} onChange={e => setNewRefund(p => ({ ...p, user_email: e.target.value }))} placeholder="user@example.com" className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="Amount (₹)"><Input type="number" value={newRefund.amount_inr} onChange={e => setNewRefund(p => ({ ...p, amount_inr: e.target.value }))} placeholder="39.00" className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="Reason"><Input value={newRefund.reason} onChange={e => setNewRefund(p => ({ ...p, reason: e.target.value }))} placeholder="Duplicate payment, etc." className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                </div>
                <Button onClick={logRefund} disabled={refundLoading} size="sm" className="bg-red-600 hover:bg-red-700 text-white"><Plus className="w-4 h-4 mr-1.5" />Log Refund</Button>

                {refunds.length > 0 && (
                  <div className="mt-5">
                    <p className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-3">Refund History ({refunds.length})</p>
                    <div className="space-y-2 max-h-64 overflow-y-auto">
                      {refunds.map(r => (
                        <div key={r.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700/50 rounded-lg text-sm">
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">{r.user_email}</p>
                            <p className="text-xs text-gray-400 font-mono">{r.payment_id}</p>
                            {r.reason && <p className="text-xs text-gray-500">{r.reason}</p>}
                          </div>
                          <span className="font-bold text-red-600 dark:text-red-400">-₹{r.amount_inr}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </Section>

              <Section title="Payment Failure Log" icon={AlertTriangle} description="Failed Razorpay payment attempts">
                {paymentFailures.length === 0 ? (
                  <p className="text-sm text-gray-400 dark:text-slate-500 text-center py-4">No payment failures recorded</p>
                ) : (
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {paymentFailures.map((f, i) => (
                      <div key={i} className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg text-sm border border-red-100 dark:border-red-900/40">
                        <p className="font-medium text-red-800 dark:text-red-300">{f.user_email || 'Unknown'}</p>
                        <p className="text-xs text-red-600 dark:text-red-400">{f.reason || 'Unknown error'} · {f.created_at ? new Date(f.created_at).toLocaleDateString() : ''}</p>
                      </div>
                    ))}
                  </div>
                )}
              </Section>
            </TabsContent>

            {/* ── EMAIL TEMPLATES ── */}
            <TabsContent value="templates">
              <Section title="Email Templates" icon={Mail} description="Edit the HTML/text used for system emails">
                <div className="space-y-3">
                  {emailTemplates.map(t => (
                    <div key={t.id} className="bg-white dark:bg-slate-800 rounded-xl border border-gray-200 dark:border-slate-700 p-4">
                      <div className="flex items-center justify-between mb-3">
                        <p className="font-semibold text-gray-900 dark:text-white text-sm">{t.name}</p>
                        <Button size="sm" variant="outline" onClick={() => setEditingTemplate({ ...t })} className="text-xs gap-1 border-gray-200 dark:border-slate-600 text-gray-700 dark:text-slate-300">
                          <Edit className="w-3.5 h-3.5" /> Edit
                        </Button>
                      </div>
                      <p className="text-xs text-gray-500 dark:text-slate-400">Subject: {t.subject}</p>
                    </div>
                  ))}
                </div>
              </Section>
            </TabsContent>

            {/* ── BULK ACTIONS ── */}
            <TabsContent value="bulk">
              <Section title="Bulk User Actions" icon={CheckSquare} description="Apply actions to multiple users at once by pasting their IDs">
                <div className="space-y-4 mb-4">
                  <Field label="User IDs" hint="Paste one ID per line, or comma-separated">
                    <textarea value={bulkIds} onChange={e => setBulkIds(e.target.value)} rows={5}
                      placeholder="user-id-1&#10;user-id-2&#10;user-id-3"
                      className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm font-mono resize-none" />
                  </Field>
                  <div className="grid sm:grid-cols-2 gap-3">
                    <Field label="Action">
                      <select value={bulkAction} onChange={e => setBulkAction(e.target.value)}
                        className="w-full h-9 px-3 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white">
                        <option value="ban">Ban Users</option>
                        <option value="shadow_ban">Shadow Ban Users</option>
                        <option value="unban">Unban Users</option>
                      </select>
                    </Field>
                    <Field label="Reason (optional)">
                      <Input value={bulkReason} onChange={e => setBulkReason(e.target.value)} placeholder="Spam, abuse, etc." className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
                    </Field>
                  </div>
                </div>
                <Button onClick={executeBulkAction} className="bg-red-600 hover:bg-red-700 text-white">
                  <CheckSquare className="w-4 h-4 mr-2" /> Execute Bulk Action
                </Button>
              </Section>
            </TabsContent>

            {/* ── BROADCAST ── */}
            <TabsContent value="broadcast">
              <Section title="Push Notification Broadcast" icon={Bell} description="Send an in-app notification to all or a segment of users">
                <div className="space-y-4 mb-4">
                  <Field label="Segment">
                    <select value={pushMsg.segment} onChange={e => setPushMsg(p => ({ ...p, segment: e.target.value }))}
                      className="w-full h-9 px-3 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm">
                      <option value="all">All Users</option>
                      <option value="premium">Premium Only</option>
                      <option value="free">Free Only</option>
                    </select>
                  </Field>
                  <Field label="Message"><Input value={pushMsg.message} onChange={e => setPushMsg(p => ({ ...p, message: e.target.value }))} placeholder="We just launched a new feature!" className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                </div>
                <Button onClick={sendPushNotification} disabled={sendingPush} className="bg-cyan-600 hover:bg-cyan-700 text-white mb-6">
                  <Bell className="w-4 h-4 mr-2" />{sendingPush ? 'Sending…' : 'Send Push Notification'}
                </Button>
              </Section>

              <Section title="Email Broadcast" icon={Mail} description="Send an email to a segment of users">
                <div className="space-y-4 mb-4">
                  <Field label="Segment">
                    <select value={broadcast.segment} onChange={e => setBroadcast(p => ({ ...p, segment: e.target.value }))}
                      className="w-full h-9 px-3 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm">
                      <option value="all">All Users</option>
                      <option value="premium">Premium Users Only</option>
                      <option value="free">Free Users Only</option>
                    </select>
                  </Field>
                  <Field label="Subject"><Input value={broadcast.subject} onChange={e => setBroadcast(p => ({ ...p, subject: e.target.value }))} placeholder="Exciting news from LitX!" className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
                  <Field label="Body">
                    <textarea value={broadcast.body} onChange={e => setBroadcast(p => ({ ...p, body: e.target.value }))} rows={6} placeholder="Write your message here…"
                      className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm resize-none" />
                  </Field>
                </div>
                <Button onClick={sendBroadcast} disabled={sendingBroadcast} className="bg-cyan-600 hover:bg-cyan-700 text-white">
                  <Mail className="w-4 h-4 mr-2" />{sendingBroadcast ? 'Sending…' : 'Send Broadcast'}
                </Button>
              </Section>
            </TabsContent>
          </Tabs>
        </div>
      </div>

      {/* ── Coupon Create Dialog ── */}
      <Dialog open={showCouponDialog} onOpenChange={setShowCouponDialog}>
        <DialogContent className="max-w-md bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white flex items-center gap-2"><Gift className="w-5 h-5 text-cyan-500" /> New Coupon</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label="Coupon Code"><Input value={newCoupon.code} onChange={e => setNewCoupon(p => ({ ...p, code: e.target.value.toUpperCase() }))} placeholder="LITX50" className="font-mono dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Discount Type">
                <select value={newCoupon.discount_type} onChange={e => setNewCoupon(p => ({ ...p, discount_type: e.target.value }))}
                  className="w-full h-9 px-3 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white">
                  <option value="percent">Percent (%)</option>
                  <option value="flat">Flat (₹)</option>
                </select>
              </Field>
              <Field label={newCoupon.discount_type === 'percent' ? 'Percent Off' : 'Amount Off (₹)'}>
                <Input type="number" value={newCoupon.discount_value} onChange={e => setNewCoupon(p => ({ ...p, discount_value: e.target.value }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
              </Field>
            </div>
            <Field label="Applicable To">
              <select value={newCoupon.applicable_to} onChange={e => setNewCoupon(p => ({ ...p, applicable_to: e.target.value }))}
                className="w-full h-9 px-3 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-sm text-gray-900 dark:text-white">
                <option value="both">Both (Premium & Promo)</option>
                <option value="premium">Premium Only</option>
                <option value="promotion">Promotion Only</option>
              </select>
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Max Uses (blank=unlimited)"><Input type="number" value={newCoupon.max_uses} onChange={e => setNewCoupon(p => ({ ...p, max_uses: e.target.value }))} placeholder="∞" className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
              <Field label="Per User Limit"><Input type="number" value={newCoupon.per_user_limit} onChange={e => setNewCoupon(p => ({ ...p, per_user_limit: parseInt(e.target.value) }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
            </div>
            <Field label="Expiry Date (optional)"><Input type="date" value={newCoupon.expires_at} onChange={e => setNewCoupon(p => ({ ...p, expires_at: e.target.value }))} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" /></Field>
            <Button onClick={createCoupon} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
              <Plus className="w-4 h-4 mr-2" /> Create Coupon
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── User Detail Dialog ── */}
      <Dialog open={!!selectedUser} onOpenChange={() => { setSelectedUser(null); setUserDetail(null); }}>
        <DialogContent className="max-w-lg bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">User Profile</DialogTitle>
          </DialogHeader>
          {userDetail ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <img src={userDetail.user.avatar_url} className="w-12 h-12 rounded-full" alt="" />
                <div>
                  <p className="font-bold text-gray-900 dark:text-white">{userDetail.user.username}</p>
                  <p className="text-sm text-gray-500 dark:text-slate-400">{userDetail.user.email}</p>
                  <div className="flex gap-2 mt-1">
                    <span className="text-xs px-2 py-0.5 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300">{userDetail.user.role}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${userDetail.user.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>{userDetail.user.status}</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Credits', value: userDetail.user.credits || 0 },
                  { label: 'Posts', value: userDetail.recent_posts.length },
                  { label: 'Reports received', value: userDetail.report_count },
                ].map(({ label, value }) => (
                  <div key={label} className="text-center p-3 bg-gray-50 dark:bg-slate-700/50 rounded-xl">
                    <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
                    <p className="text-xs text-gray-400 dark:text-slate-500">{label}</p>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Button size="sm" onClick={() => setGrantDialog({ type: 'credits', userId: userDetail.user.id })} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"><Zap className="w-3.5 h-3.5 mr-1.5" /> Grant Credits</Button>
                <Button size="sm" onClick={() => setGrantDialog({ type: 'premium', userId: userDetail.user.id })} className="flex-1 bg-purple-600 hover:bg-purple-700 text-white"><Crown className="w-3.5 h-3.5 mr-1.5" /> Grant Premium</Button>
              </div>
              {userDetail.recent_posts.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 dark:text-slate-300 mb-2">Recent Posts</p>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {userDetail.recent_posts.map(p => (
                      <div key={p.id} className="p-2.5 bg-gray-50 dark:bg-slate-700/50 rounded-lg text-sm text-gray-700 dark:text-slate-300 line-clamp-2">{p.content}</div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : <p className="text-center py-8 text-gray-400">Loading…</p>}
        </DialogContent>
      </Dialog>

      {/* ── Email Template Edit Dialog ── */}
      <Dialog open={!!editingTemplate} onOpenChange={() => setEditingTemplate(null)}>
        <DialogContent className="max-w-lg bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white flex items-center gap-2">
              <Mail className="w-5 h-5 text-cyan-500" /> Edit: {editingTemplate?.name}
            </DialogTitle>
          </DialogHeader>
          {editingTemplate && (
            <div className="space-y-4">
              <Field label="Subject">
                <Input value={editingTemplate.subject} onChange={e => setEditingTemplate(p => ({ ...p, subject: e.target.value }))}
                  className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
              </Field>
              <Field label="Body" hint="Use {{code}}, {{reset_url}} as placeholders">
                <textarea value={editingTemplate.body} onChange={e => setEditingTemplate(p => ({ ...p, body: e.target.value }))}
                  rows={8} className="w-full px-3 py-2 rounded-md border border-gray-200 dark:border-slate-600 bg-white dark:bg-slate-700 text-gray-900 dark:text-white text-sm font-mono resize-none" />
              </Field>
              <Button onClick={() => saveEmailTemplate(editingTemplate)} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
                <Save className="w-4 h-4 mr-2" /> Save Template
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* ── Grant Dialog ── */}
      <Dialog open={!!grantDialog} onOpenChange={() => { setGrantDialog(null); setGrantAmount(''); }}>
        <DialogContent className="max-w-sm bg-white dark:bg-slate-800 border-gray-200 dark:border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-gray-900 dark:text-white">
              {grantDialog?.type === 'credits' ? '⚡ Grant Credits' : '👑 Grant Premium'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Field label={grantDialog?.type === 'credits' ? 'Number of credits' : 'Number of days'}>
              <Input type="number" value={grantAmount} onChange={e => setGrantAmount(e.target.value)} placeholder={grantDialog?.type === 'credits' ? 'e.g. 5' : 'e.g. 30'} className="dark:bg-slate-700 dark:border-slate-600 dark:text-white" />
            </Field>
            <Button onClick={handleGrant} className="w-full bg-cyan-600 hover:bg-cyan-700 text-white">
              <ChevronRight className="w-4 h-4 mr-1.5" /> Confirm Grant
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}